import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Alert, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Storefront, TrendingUp, Inventory2, ReceiptLong, LocalOffer } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import StudioSelector, { StudioOption } from './StudioSelector';

const rupee = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

/** YYYY-MM-DD for a date N days ago — the format the API's from/to expect. */
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

interface Props {
  selectedStudio: StudioOption | null;
  onStudioChange: (s: StudioOption | null) => void;
  onToast: (msg: string, severity: 'success' | 'error') => void;
}

/**
 * Merchandise revenue — reported here, deliberately apart from the studio's
 * session/pack revenue.
 *
 * Merch transactions carry `merchandiseOrder` and never `booking`/`purchasedPack`,
 * so they are structurally excluded from every payout, statement and invoice
 * query. That is by design: retail is a parallel ledger, not a settlement line.
 * This tab is the only place it surfaces.
 */
export default function MerchandiseRevenueTab({ selectedStudio, onStudioChange, onToast }: Props) {
  const { admin } = useAuth();
  const isStudio = admin?.role !== 'super_admin' && admin?.role !== 'admin';
  const [range, setRange] = useState('30');
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const studioId = selectedStudio?._id;

  const applyRange = (r: string) => {
    setRange(r);
    if (r !== 'custom') {
      setFrom(daysAgo(Number(r)));
      setTo(new Date().toISOString().slice(0, 10));
    }
  };

  const fetchRevenue = useCallback(async () => {
    if (!isStudio && !studioId) { setData(null); return; }
    setLoading(true);
    try {
      const res: any = await apiService.getMerchandiseRevenue({
        ...(studioId ? { studioId } : {}),
        from,
        to,
      });
      setData(res);
    } catch (err: any) {
      onToast(err?.response?.data?.message || 'Failed to load revenue', 'error');
    } finally {
      setLoading(false);
    }
  }, [isStudio, studioId, from, to, onToast]);

  useEffect(() => { fetchRevenue(); }, [fetchRevenue]);

  const r = data?.revenue;

  const tiles = [
    { label: 'Gross revenue', value: rupee(r?.gross ?? 0), icon: <TrendingUp />, hint: 'Incl. GST' },
    { label: 'Net of tax', value: rupee(r?.net ?? 0), icon: <ReceiptLong />, hint: 'Ex-GST, after discounts' },
    { label: 'GST collected', value: rupee(r?.taxes ?? 0), icon: <ReceiptLong />, hint: 'Payable' },
    { label: 'Discounts given', value: rupee(r?.discounts ?? 0), icon: <LocalOffer />, hint: 'Coupons' },
    { label: 'Orders', value: String(r?.orders ?? 0), icon: <Storefront />, hint: 'Excl. cancelled' },
    { label: 'Units sold', value: String(r?.units ?? 0), icon: <Inventory2 />, hint: 'Total items' },
  ];

  return (
    <Box>
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <StudioSelector value={selectedStudio} onChange={onStudioChange} required />
        <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => v && applyRange(v)}>
          <ToggleButton value="7">7d</ToggleButton>
          <ToggleButton value="30">30d</ToggleButton>
          <ToggleButton value="90">90d</ToggleButton>
          <ToggleButton value="365">1y</ToggleButton>
          <ToggleButton value="custom">Custom</ToggleButton>
        </ToggleButtonGroup>
        {range === 'custom' && (
          <>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              value={from} onChange={e => setFrom(e.target.value)} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              value={to} onChange={e => setTo(e.target.value)} />
          </>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !isStudio && !studioId ? (
        <Box textAlign="center" py={10}>
          <Storefront sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">Select a studio to view its merchandise revenue</Typography>
        </Box>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Merchandise revenue is tracked separately from session and pack revenue,
            and is <strong>not</strong> part of studio payout statements.
          </Alert>

          <Grid container spacing={2} mb={3}>
            {tiles.map(t => (
              <Grid key={t.label} size={{ xs: 6, sm: 4, md: 2 }}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={0.75} color="text.secondary" mb={1}>
                      {React.cloneElement(t.icon, { sx: { fontSize: 16 } })}
                      <Typography variant="caption" fontWeight={700}>{t.label}</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700}>{t.value}</Typography>
                    <Typography variant="caption" color="text.disabled">{t.hint}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} mb={1}>Top products</Typography>
          {!data?.topProducts?.length ? (
            <Box textAlign="center" py={6}>
              <Typography color="text.secondary" variant="body2">No sales in this period</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><Typography variant="caption" fontWeight={700}>Product</Typography></TableCell>
                    <TableCell align="right"><Typography variant="caption" fontWeight={700}>Units</Typography></TableCell>
                    <TableCell align="right"><Typography variant="caption" fontWeight={700}>Revenue (ex-GST)</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.topProducts.map((p: any, i: number) => (
                    <TableRow key={p.merchandiseId ?? i} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {i < 3 && <Chip size="small" label={`#${i + 1}`} color="primary" sx={{ height: 18, fontSize: 10 }} />}
                          <Typography variant="body2">{p.title}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2">{p.units}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight={600}>{rupee(p.revenue)}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
